import { KeysOfType, CollectionTypes, CollectionModels } from ".."
import {
  Answer,
  AssetRef,
  BlockRef,
  ExerciseStateMap,
  Question,
  Feedback,
  Hint,
  TypeArray
} from "../types"

// Import action creator helpers from the separate file
import {
  actionCreatorTypeGuard,
  actionCreatorGuard,
  reducerActions,
  handler
} from "./actionCreators"

// Import reducer guard helpers from the separate file
import { reducerTypeGuard, reducerGuard, getIdxKeyGuard } from "./reducerGuards"

// Re-export the utility functions for use in other files
export {
  actionCreatorTypeGuard,
  actionCreatorGuard,
  reducerTypeGuard,
  reducerGuard,
  getIdxKeyGuard
}

export type { reducerActions }

// Import all reducers and actions from types
import { assetRefReducer, assetRefAction } from "../types/AssetRef"
import { answerReducer, answerAction } from "../types/Answer"
import { blockRefReducer, blockRefAction } from "../types/BlockRef"
import { durationReducer, durationAction, Duration } from "../types/Duration"
import {
  elementStateReducer,
  elementStateAction,
  ElementState
} from "../types/ElementState"
import {
  exerciseStateReducer,
  exerciseStateAction,
  ExerciseState
} from "../types/ExerciseState"
import { feedbackReducer, feedbackAction } from "../types/Feedback"
import { questionReducer, questionAction } from "../types/Question"
import { hintReducer, hintAction } from "../types/Hint"
import { seqRefReducer, seqRefAction, SeqRef } from "../types/SeqRef"
import { snapRefReducer, snapRefAction, SnapRef } from "../types/SnapRef"
import {
  snapShotRefReducer,
  snapShotRefAction,
  SnapShotRef
} from "../types/SnapShotRef"
import {
  screenStateReducer,
  screenStateAction,
  ScreenState
} from "../types/ScreenState"
import {
  blockStateReducer,
  blockStateAction,
  BlockState
} from "../types/BlockState"
import {
  answerStateReducer,
  answerStateAction,
  AnswerState
} from "../types/AnswerState"
import {
  questionStateReducer,
  questionStateAction,
  QuestionState
} from "../types/QuestionState"
import {
  feedbackStateReducer,
  feedbackStateAction,
  FeedbackState
} from "../types/FeedbackState"
import { settingsReducer, settingsAction, Settings } from "../types/Settings"
import { snapPathReducer, snapPathAction, SnapPath } from "../types/SnapPath"
import { otherReducer, otherAction, Other } from "../types/Other"
import { jumpReducer, jumpAction, Jump } from "../types/Jump"
import {
  questionTargetReducer,
  questionTargetAction,
  QuestionTarget
} from "../types/QuestionTarget"
import { ttsReducer, ttsAction, TTS } from "../types/TTS"

import _ from "lodash"
import { Explanation, explanationReducer } from "../types/Explanation"

// Define typeReducers
export const typeReducers = {
  Answer: answerReducer,
  AnswerState: answerStateReducer,
  Explanation: explanationReducer,
  AssetRef: assetRefReducer,
  BlockRef: blockRefReducer,
  BlockState: blockStateReducer,
  Settings: settingsReducer,
  Duration: durationReducer,
  ElementState: elementStateReducer,
  ExerciseState: exerciseStateReducer,
  Feedback: feedbackReducer,
  FeedbackState: feedbackStateReducer,
  Hint: hintReducer,
  Jump: jumpReducer,
  Other: otherReducer,
  Question: questionReducer,
  QuestionState: questionStateReducer,
  QuestionTarget: questionTargetReducer,
  ScreenState: screenStateReducer,
  SeqRef: seqRefReducer,
  SnapPath: snapPathReducer,
  SnapRef: snapRefReducer,
  SnapShotRef: snapShotRefReducer,
  TTS: ttsReducer,
  TypeArray: null
}

// TypeScript type for typeReducers keys
export type TypeReducerKey = keyof typeof typeReducers

export function getArrayTypeReducerGuard<Model, T>(
  key: string,
  prop: KeysOfType<Model, Array<T>>,
  reducer: TypeReducerKey,
  state: any,
  action: any
) {
  const x = arrayReducerGuard<Model, T>(
    state[prop],
    action,
    typeReducers[reducer] as (x: T, y: any) => T,
    key,
    prop
  )
  return { ...state, [prop]: [...x] }
}

export function getObjectTypeReducerGuard<Model, T>(
  key: string,
  prop: KeysOfType<Model, T>,
  reducer: TypeReducerKey,
  state: any,
  action: any
) {
  const x = objectReducerGuard<Model, T>(
    state[prop],
    action,
    typeReducers[reducer] as (x: T, y: any) => T,
    key,
    prop
  )
  return { ...state, [prop]: x }
}

export function getObjectMapTypeReducerGuard<Model, T, M>(
  key: string,
  prop: KeysOfType<Model, T>,
  reducer: TypeReducerKey,
  state: any,
  action: any
) {
  const x = objectReducerGuard<Model, T>(
    state[prop],
    action,
    typeReducers[reducer] as (x: T, y: any) => T,
    key,
    prop
  )
  return { ...state, [prop]: x }
}

export function getArrayReducerGuard<Model, T>(
  kind: KeysOfType<CollectionTypes, Model>,
  prop: KeysOfType<Model, Array<T>>,
  reducer: TypeReducerKey,
  state: any,
  action: any
) {
  const x = arrayReducerGuard<Model, T>(
    state[prop],
    action,
    typeReducers[reducer] as (x: T, y: any) => T,
    kind,
    prop
  )
  return { ...state, [prop]: [...x] }
}

export function processActionMeta(action: any) {
  const mutableAction = _.cloneDeep(action)
  const { meta } = mutableAction

  if (meta?.nodes && Array.isArray(meta.nodes)) {
    const [prop, idx] = meta.nodes.shift() as [string, number]
    return { ...action, meta: { nodes: meta.nodes, prop: prop, idx: idx } }
  }
  return action
}

export function objectReducerGuard<Model, T>(
  state: T,
  action: {
    type: string
    payload: T | null | undefined
    meta?: { [key: string]: any }
  },
  reducer: (state: T, action: any) => T,
  key: string,
  prop: KeysOfType<Model, T>
): T {
  // Ensure idx == null, otherwise we are in the wrong reducer
  if (
    action?.meta?.idx === null &&
    action?.meta?.nodes &&
    Array.isArray(action.meta.nodes)
  ) {
    let o: T = { ...state }
    // are we at our target node?
    if (action.meta.nodes.length == 0) {
      if (action.type === "__record/delete") {
        console.log("Error: cannot delete an object record type.")
      } else if (action.type === "__record/null") {
        if (action.payload === null) {
          o = null as any
        } else {
          console.log("Error: payload mismatch for nulling record.")
        }
      } else if (
        action.type.startsWith("__record") &&
        action?.payload !== undefined
      ) {
        // new record
        o = action.payload as T
      } else {
        // ensure the idx is in bounds, otherwise bad stuff happens (-;
        if (action.type.startsWith("__create")) {
          const [_a, type, _b] = action.type.split("/")
          if (type) {
            if (typeof action.payload == "object") {
              o = action.payload as T
            } else {
              console.log("Error: not an object, can't assign: ", type)
            }
          } else {
            console.log("Error: empty record type: ", type)
          }
        } else {
          o = reducer(o, action)
        }
      }
    } else {
      // not a target node, so keep on keeping on
      o = reducer(o, action)
    }

    // return new object
    return o
  } else if (
    typeof action?.meta?.idx === "string" &&
    action?.meta?.nodes &&
    Array.isArray(action.meta.nodes)
  ) {
    // Ok, we have an object map
    const o: { [key: string]: any } = { ...state }

    // are we at our target node?
    if (action.meta.nodes.length == 0) {
      if (action.type === "__record/delete") {
        console.log("Error: cannot delete an object record type.")
      } else if (action.type === "__record/null") {
        if (action.payload === null) {
          o[action.meta.idx] = null as any
        } else {
          console.log("Error: payload mismatch for nulling record.")
        }
      } else if (
        action.type.startsWith("__record") &&
        action?.payload !== undefined
      ) {
        // new record
        o[action.meta.idx] = action.payload as T
      } else {
        // ensure the idx is in bounds, otherwise bad stuff happens (-;
        if (action.type.startsWith("__create")) {
          const [_a, type, _b] = action.type.split("/")
          if (type) {
            if (typeof action.payload === "object") {
              o[action.meta.idx] = action.payload as T
            } else {
              console.log("Error: not an object, can't assign: ", type)
            }
          } else {
            console.log("Error: empty record type: ", type)
          }
        } else {
          o[action.meta.idx] = reducer(o[action.meta.idx], action)
        }
      }
    } else {
      // not a target node, so keep on keeping on
      o[action.meta.idx] = reducer(o[action.meta.idx], action)
    }

    // return new object
    return o as any
  }

  if (!(action?.meta?.idx === null || typeof action?.meta?.idx === "string")) {
    console.log(
      "Error: called node for object, with idx not set to null or string"
    )
  }

  // Nothing to see here
  return state
}

export function arrayReducerGuard<Model, T>(
  state: Array<T>,
  action: {
    type: string
    payload: T | null | undefined
    meta?: { [key: string]: any }
  },
  reducer: (state: T, action: any) => T,
  key: string,
  prop: KeysOfType<Model, Array<T>>
): Array<T> {
  // Do we have an idx? is it a number? Do we have nodes? Is it an array?
  if (
    typeof action?.meta?.idx === "number" &&
    action?.meta?.nodes &&
    Array.isArray(action.meta.nodes)
  ) {
    const a: Array<T> = [...state]

    // are we at our target node?
    if (action.meta.nodes.length == 0) {
      // we will allow nulls to be pushed onto an array, but not an undefined
      if (
        action.type != "__record/delete" &&
        action.type !== "__record/null" &&
        action.type.startsWith("__record") &&
        action?.payload !== undefined
      ) {
        // if idx is -1 just push
        if (action.meta.idx == -1) {
          a.push(action.payload as T)
        } else if (action.meta.idx >= 0) {
          // idx is positive -- not can make huge sparse array
          a[action.meta.idx] = action.payload as T
        }
      } else if (action.type === "__record/delete") {
        // idx in bounds?
        if (action.meta.idx < state.length && action.meta.idx >= 0) {
          // delete the records default to 1 record if payload is not a number
          a.splice(
            action.meta.idx,
            typeof action.payload == "number" ? action.payload : 1
          )
        } else {
          // delete for a non-existent record
          console.log(
            "Error: Recieved a delete request for non-existent record: %o",
            action
          )
        }
      } else if (action.type === "__record/null") {
        if (action.payload === null) {
          if (action.meta.idx < state.length && action.meta.idx >= 0) {
            // null the entry
            a[action.meta.idx] = null as any
          } else {
            // null for a non-existent record
            console.log(
              "Error: Recieved a null request for non-existent record: %o",
              action
            )
          }
        } else {
          console.log("Error: payload mismatch for nulling record.")
        }
      } else {
        if (action.type.startsWith("__create")) {
          const [_a, type, _b] = action.type.split("/")
          if (type) {
            if (typeof action.payload == "object") {
              if (action.meta.idx == -1) {
                a.push(action.payload as T)
              } else {
                a[action.meta.idx] = action.payload as T
              }
            } else {
              console.log("Error: not an object, can't assign: ", type)
            }
          } else {
            console.log("Error: empty record type: ", type)
          }
        } else if (action.meta.idx >= 0 && action.meta.idx < a.length) {
          a[action.meta.idx] = reducer(a[action.meta.idx], action)
        } else {
          console.log("Error: don't have any further to go. :(")
        }
      }
    } else {
      // ensure idx is in bounds, otherwise bad stuff happens (-;
      if (action.meta.idx >= 0 && action.meta.idx < a.length) {
        a[action.meta.idx] = reducer(a[action.meta.idx], action)
      } else {
        console.log("Request of idx not in bounds: %o", action)
      }
    }

    // return new array
    return a
  }

  if (typeof action?.meta?.idx !== "number") {
    console.log("Error: called node for array, with idx not a number")
  }

  return state
}

// Define model types using the type names without circular references
export type TypeModels =
  | Answer
  | AnswerState
  | AssetRef
  | BlockRef
  | BlockState
  | Settings
  | Duration
  | ExerciseState
  | Feedback
  | FeedbackState
  | Hint
  | Jump
  | Other
  | Question
  | QuestionState
  | QuestionTarget
  | ScreenState
  | SeqRef
  | SnapPath
  | SnapRef
  | SnapShotRef

export type TypeTypes = {
  answers: Answer
  answerStates: AnswerState
  assets: AssetRef
  blockStates: BlockState
  settings: Settings
  durations: Duration
  elements: ElementState
  exercises: ExerciseState
  feedbacks: Feedback
  feedbackStates: FeedbackState
  answerFeedbacks: Feedback
  answerFeedbackStates: FeedbackState
  generalFeedbacks: Feedback
  generalFeedbackStates: FeedbackState
  nonzoneFeedbackStates: FeedbackState
  hints: Hint
  jump: Jump
  left: BlockRef
  leftState: BlockState
  other: Other
  questions: Question
  questionStates: QuestionState
  right: BlockRef
  rightState: BlockState
  screenStates: ScreenState
  sequences: SeqRef
  snaps: SnapRef
  snapPaths: SnapPath
  target: QuestionTarget
  versions: SnapShotRef
}

export const typeActions = {
  answers: answerAction,
  answerStates: answerStateAction,
  assets: assetRefAction,
  blockStates: blockStateAction,
  settings: settingsAction,
  durations: durationAction,
  elements: elementStateAction,
  exercises: exerciseStateAction,
  feedbacks: feedbackAction,
  feedbackStates: feedbackStateAction,
  answerFeedbacks: feedbackAction,
  answerFeedbackStates: feedbackStateAction,
  generalFeedbacks: feedbackAction,
  generalFeedbackStates: feedbackStateAction,
  nonzoneFeedbackStates: feedbackStateAction,
  hints: hintAction,
  jump: jumpAction,
  left: blockRefAction,
  leftState: blockStateAction,
  other: otherAction,
  questions: questionAction,
  questionStates: questionStateAction,
  right: blockRefAction,
  rightState: blockStateAction,
  screenStates: screenStateAction,
  sequences: seqRefAction,
  snapPaths: snapPathAction,
  snaps: snapRefAction,
  target: questionTargetAction,
  versions: snapShotRefAction
}

type AnyFunction = (...args: any[]) => any
type ReplaceReturnType<T extends (...a: any) => any, TNewReturn> = (
  ...a: Parameters<T>
) => TNewReturn

export type TypeActions = typeof typeActions
export type TypeDispatchedActions<T extends keyof TypeActions> = {
  [key in keyof TypeActions[T]]: ReplaceReturnType<
    TypeActions[T][key] extends AnyFunction ? TypeActions[T][key] : never,
    void
  >
}

export function dispatchWrapperForActionsOfTypes<K extends keyof TypeActions>(
  types: K,
  meta: { nodes: Array<[string, number | null | string]> },
  dispatch: any
) {
  const wrap = <Func extends AnyFunction>(
    fn: Func
  ): ((...args: Parameters<Func>) => void) => {
    const wrappedFn = (...args: Parameters<Func>): void => {
      //args[0] payload
      //args[1] additional meta data:
      //eg: node.__create(null, { castType: type })
      if (args[1]) {
        meta = {
          ...meta,
          ...args[1]
        }
      }
      return dispatch({ ...fn(...args), meta: meta })
    }
    return wrappedFn
  }

  const wrappedActions = Object.fromEntries(
    Object.entries(typeActions[types]).map(k => [
      k[0],
      wrap<(typeof k)[1]>(k[1])
    ])
  ) as unknown as TypeDispatchedActions<K>

  return wrappedActions
}

export interface GetNodefn<Model> {
  <K extends keyof TypeActions, N extends keyof Model>(
    node: K extends keyof Model ? K : never,
    idx: number | string | null,
    nodes?: Array<[string, number]>
  ): TypeDispatchedActions<K> & {
    node: GetNodefn<TypeTypes[K] extends TypeModels ? TypeTypes[K] : never>
  }
}

type Overlap<T, U> = { [K in Extract<keyof T, keyof U>]: U[K] }

export function dispatchWrapperForNode<
  Model extends CollectionModels | TypeModels,
  N extends keyof Model
>(dispatch: any, nodes: Array<[string, number | string | null]> = []) {
  function getNode<K extends keyof Overlap<TypeActions, Model>>(
    node: K,
    idx: number | string | null = null
  ) {
    //: TypeDispatchedActions<K> & { node: GetNodefn<Model[N]>} {
    const n: Array<[string, number | string | null]> = [...nodes]

    // if (typeof idx === 'string') idx = parseInt(idx)
    n.push([node, idx])

    const actions = dispatchWrapperForActionsOfTypes(
      node,
      { nodes: n },
      dispatch
    ) as TypeDispatchedActions<K>

    return {
      ...actions,
      node: dispatchWrapperForNode<
        Model[N] extends CollectionModels | TypeModels ? Model[N] : never,
        keyof Model[N]
      >(dispatch, n)
    } as unknown as TypeDispatchedActions<K> & {
      node: GetNodefn<TypeTypes[K] extends TypeModels ? TypeTypes[K] : never>
    }
  }

  return getNode
}
